import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertTriangle,
  AlertCircle,
  Search,
  MoreHorizontal,
  CheckCircle,
  ArrowUpRight,
  Clock,
  Loader2,
  Eye,
  Bell,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const severityConfig = {
  critical: { label: 'Critical', class: 'bg-red-100 text-red-700 border-red-200', icon: AlertCircle },
  high: { label: 'High', class: 'bg-orange-100 text-orange-700 border-orange-200', icon: AlertTriangle },
  medium: { label: 'Medium', class: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock },
  low: { label: 'Low', class: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
};

const statusConfig = {
  pending: { label: 'Pending', class: 'bg-amber-100 text-amber-700' },
  acknowledged: { label: 'Acknowledged', class: 'bg-blue-100 text-blue-700' },
  escalated: { label: 'Escalated', class: 'bg-purple-100 text-purple-700' },
  resolved: { label: 'Resolved', class: 'bg-green-100 text-green-700' },
  closed: { label: 'Closed', class: 'bg-slate-100 text-slate-700' },
};

export const AlertInboxPage = () => {
  const { api, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || 'pending',
    severity: searchParams.get('severity') || '',
  });

  useEffect(() => {
    fetchAlerts();
  }, [page, filters]);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (filters.status) params.append('status', filters.status);
      if (filters.severity) params.append('severity', filters.severity);

      const response = await api.get(`/alerts?${params.toString()}`);
      setAlerts(response.data.alerts || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await api.post(`/alerts/${alertId}/acknowledge`);
      toast.success('Alert acknowledged');
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleEscalate = async (alertId) => {
    try {
      await api.post(`/alerts/${alertId}/escalate`);
      toast.success('Alert escalated');
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to escalate alert');
    }
  };

  const handleResolve = async (alertId) => {
    try {
      await api.post(`/alerts/${alertId}/resolve`);
      toast.success('Alert resolved');
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
    if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }
    setSearchParams(searchParams);
  };

  const getAlertAge = (createdAt) => {
    try {
      return formatDistanceToNow(new Date(createdAt), { addSuffix: true });
    } catch {
      return '-';
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="alert-inbox-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Alert Inbox</h1>
          <p className="text-muted-foreground mt-1">{total} alerts total</p>
        </div>
      </div>

      {/* Severity Summary Tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {['critical', 'high', 'medium', 'low'].map((severity) => {
          const count = alerts.filter((a) => a.severity === severity && a.status === 'pending').length;
          const Icon = severityConfig[severity].icon;
          return (
            <Card
              key={severity}
              className={`cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md ${
                filters.severity === severity ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => handleFilterChange('severity', filters.severity === severity ? '' : severity)}
              data-testid={`severity-filter-${severity}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    severity === 'critical' ? 'bg-red-100' :
                    severity === 'high' ? 'bg-orange-100' :
                    severity === 'medium' ? 'bg-yellow-100' : 'bg-green-100'
                  }`}>
                    <Icon className={`w-5 h-5 ${
                      severity === 'critical' ? 'text-red-600' :
                      severity === 'high' ? 'text-orange-600' :
                      severity === 'medium' ? 'text-yellow-600' : 'text-green-600'
                    }`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-sm text-muted-foreground capitalize">{severity}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search alerts..." className="pl-10" data-testid="search-input" />
            </div>
            <Select value={filters.status || "all"} onValueChange={(value) => handleFilterChange('status', value === "all" ? "" : value)}>
              <SelectTrigger className="w-[160px]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
                <SelectItem value="escalated">Escalated</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Alerts Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : alerts.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Severity</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Age</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alerts.map((alert) => {
                    const SeverityIcon = severityConfig[alert.severity]?.icon || Bell;
                    return (
                      <TableRow key={alert.alert_id} data-testid={`alert-row-${alert.alert_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <SeverityIcon className={`w-4 h-4 ${
                              alert.severity === 'critical' ? 'text-red-600' :
                              alert.severity === 'high' ? 'text-orange-600' :
                              alert.severity === 'medium' ? 'text-yellow-600' : 'text-green-600'
                            }`} />
                            <Badge className={`border ${severityConfig[alert.severity]?.class}`}>
                              {severityConfig[alert.severity]?.label}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm font-medium max-w-md truncate">{alert.message}</p>
                          <p className="text-xs text-muted-foreground">{alert.alert_type}</p>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{alert.patient_name || '-'}</p>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusConfig[alert.status]?.class}>
                            {statusConfig[alert.status]?.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-muted-foreground">{getAlertAge(alert.created_at)}</p>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`alert-actions-${alert.alert_id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {alert.report_id && (
                                <DropdownMenuItem asChild>
                                  <Link to={`/reports/${alert.report_id}`}>
                                    <Eye className="w-4 h-4 mr-2" />
                                    View Report
                                  </Link>
                                </DropdownMenuItem>
                              )}
                              {alert.status === 'pending' && (
                                <DropdownMenuItem onClick={() => handleAcknowledge(alert.alert_id)}>
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Acknowledge
                                </DropdownMenuItem>
                              )}
                              {(alert.status === 'pending' || alert.status === 'acknowledged') && (
                                <DropdownMenuItem onClick={() => handleEscalate(alert.alert_id)}>
                                  <ArrowUpRight className="w-4 h-4 mr-2" />
                                  Escalate
                                </DropdownMenuItem>
                              )}
                              {alert.status !== 'resolved' && alert.status !== 'closed' && (
                                <DropdownMenuItem onClick={() => handleResolve(alert.alert_id)}>
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Resolve
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64">
              <Bell className="w-12 h-12 text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">No alerts found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
